import React, { ReactNode } from 'react';
import { useTracking } from 'react-tracking';
import {
  AnyTag,
  Folder,
  Message,
  PerspectiveTag,
  Tag,
  TagsMetadata,
  TextAnalyzerTag
} from '../../../state/types';
import DropdownLink from '../../../../../common/dropdown-link';
import { getTagText } from '../../header/tag-tooltip';
import TagBadge from './tag';
import ManualTagTooltip from './manual-tag-tooltip';
import { isMessageWithRichText } from 'utils/validators';
import { SelectableTag } from './tag/SelectableTag';
import { getSelectableTagInfo } from './message-tags-utils';
import { TagInfo } from './tag-info/TagInfo';
import { useTags } from '../../tags/useTags';
import { stripPTag } from '../../../utils/domUtils';

const NUMBER_OF_TAGS_TO_DISPLAY = 4;
const GOOGLE_PERSPECTIVE = 'google_perspective';
const OW_MODELS = 'ow_models';

interface Props {
  message: Message;
  folder: Folder;
  openAuditLogModal: () => void;
  isHighlightTagsEnabled: boolean;
  setHighlightTag: (
    message: Message,
    tagName: TextAnalyzerTag,
    vendorType: PerspectiveTag.GOOGLE_PERSPECTIVE
  ) => void;
}

const SELECTABLE_TAGS = [
  Tag.APPEAL,
  Tag.APPEAL_REPORTER,
  Tag.REPORTED,
  Tag.STAFF_REPORT,
  Tag.AI_MODERATED
];

const MessageTags: React.FC<Props> = ({
  message,
  folder,
  setHighlightTag,
  isHighlightTagsEnabled,
  openAuditLogModal
}) => {
  const tracking = useTracking();
  const tags: AnyTag[] = message.get('tags', []);
  const tagsData: TagsMetadata = message.get('tagsData', {});
  const highlightedTagSpans: any = message.get('highlightedTagSpans', {});
  const shouldEnableHighlightTags =
    !isMessageWithRichText(stripPTag(message.getText())) && isHighlightTagsEnabled;
  const selectableTags = tags.filter((tag: Tag) => SELECTABLE_TAGS.includes(tag)) as Tag[];
  const [selectedTag, setSelectedTag] = React.useState<Tag>(selectableTags[0]);

  const tagsDataArray = useTags();

  // TODO: extract to service
  const convertCase = word => {
    const lower = word.toLowerCase();
    return lower.replace(/(^| )(\w)/g, x => x.toUpperCase()).replace(/_/g, ' ');
  };

  const onTextAnalyzerTagClick = (
    tagName: TextAnalyzerTag,
    vendorType: PerspectiveTag.GOOGLE_PERSPECTIVE
  ) => {
    if (!shouldEnableHighlightTags) return;
    setHighlightTag(message, tagName, vendorType);
    tracking.trackEvent({ eventName: 'highlight tag clicked', vendorType });
  };

  const getPerspectiveTags = (): React.ReactElement[] => {
    const perspectiveTags: Record<PerspectiveTag, string> =
      tagsData.text_analyzer?.['perspective'] || tagsData[GOOGLE_PERSPECTIVE];

    return Object.keys(perspectiveTags || {}).map((tag: PerspectiveTag) => (
      <TagBadge
        key={`perspective_${tag}`}
        onClick={() => onTextAnalyzerTagClick(tag, PerspectiveTag.GOOGLE_PERSPECTIVE)}
        color="red"
        type={tag}
        text={convertCase(tag)}
        tooltip={`Flagged at ${perspectiveTags[tag]}`}
        isTagActive={highlightedTagSpans?.selectedTagName === tag}
        shouldAddClickableIcon={shouldEnableHighlightTags}
      />
    ));
  };

  const getOWTags = (): React.ReactElement[] => {
    const owTags: Record<string, string> = tagsData.text_analyzer?.[OW_MODELS];

    return Object.keys(owTags || {}).map(tag => (
      <TagBadge
        key={tag}
        color="red"
        type={tag}
        text={convertCase(getTagText(tag, tagsDataArray))}
        tooltip={`Flagged at ${owTags[tag]}`}
      />
    ));
  };

  const getBaseTags = (): React.ReactNode[] | Array<null> => {
    let foundRestrictedWord = false;
    let foundInvalidCharTag = false;

    return tags.map((tag: Tag) => {
      let component: ReactNode = null;
      switch (tag) {
        // Special tags
        case Tag.RESTRICTED_WORDS:
        case Tag.REJECTED_WORDS:
        case Tag.PUBLISH_AND_MODERATE_WORDS:
          if (!foundRestrictedWord) {
            foundRestrictedWord = true;
            component = (
              <TagBadge
                key={Tag.RESTRICTED_WORDS}
                type={Tag.RESTRICTED_WORDS}
                color="yellow"
                tooltip="Comment contains restricted words"
              />
            );
          }
          break;
        case Tag.HIDDEN_CHARACTERS:
          component = (
            <TagBadge key={Tag.HIDDEN_CHARACTERS} type={Tag.HIDDEN_CHARACTERS} color="yellow" />
          );
          break;
        case Tag.MANUAL:
          const manuallyTooltip = (
            <ManualTagTooltip
              folder={folder}
              openAuditLogModal={openAuditLogModal}
              messageId={message.get('id')}
            />
          );

          component = (
            <TagBadge key={'manually'} color={'black'} text="Manual" tooltip={manuallyTooltip} />
          );
          break;

        case Tag.PORN_LINK:
          component = (
            <TagBadge
              key={Tag.PORN_LINK}
              type={Tag.PORN_LINK}
              color="red"
              tooltip="Comment contains porn link/s"
            />
          );
          break;

        case Tag.BLACKLISTED_IP:
          component = (
            <TagBadge
              key={Tag.BLACKLISTED_IP}
              type={Tag.BLACKLISTED_IP}
              color="red"
              tooltip="Comment contains blocklist IP/'s"
            />
          );
          break;
        case Tag.STRICT_ARTICLE:
          component = (
            <TagBadge
              key={Tag.STRICT_ARTICLE}
              type={Tag.STRICT_ARTICLE}
              text="Strict"
              color="grey"
              tooltip="Comment submitted while the conversation was set to strict moderation"
            />
          );
          break;
        case Tag.BLACKLISTED_USER:
          component = (
            <TagBadge
              key={Tag.BLACKLISTED_USER}
              type={Tag.BLACKLISTED_USER}
              color="red"
              tooltip="Comment from blocklist user"
            />
          );
          break;
        case Tag.EXTERNAL_LINK:
          component = (
            <TagBadge
              key={Tag.EXTERNAL_LINK}
              type={Tag.EXTERNAL_LINK}
              color="red"
              tooltip="Comment contains external link/s"
            />
          );
          break;
        case Tag.IMAGE:
          component = (
            <TagBadge
              key={Tag.IMAGE}
              type={Tag.IMAGE}
              color="red"
              tooltip="Comment contains image/s"
            />
          );
          break;
        case Tag.SPAM:
          if (tags.includes(PerspectiveTag.SPAM) && !tags.includes(Tag.AKISMET)) {
            break;
          }
          component = (
            <TagBadge
              key={Tag.SPAM}
              type={Tag.SPAM}
              color="red"
              tooltip="Comment is suspected as spam"
            />
          );
          break;
        case Tag.DUPLICATION:
          component = (
            <TagBadge
              key={Tag.DUPLICATION}
              type={Tag.DUPLICATION}
              color="red"
              tooltip="Comment Repeated"
            />
          );
          break;
        case Tag.MALICIOUS:
          component = (
            <TagBadge
              key={Tag.MALICIOUS}
              type={Tag.MALICIOUS}
              color="red"
              tooltip="Comment contains malicious link/s"
            />
          );
          break;
        case Tag.RESTRICTED_LANGUAGE:
          component = (
            <TagBadge
              key={Tag.RESTRICTED_LANGUAGE}
              type={Tag.RESTRICTED_LANGUAGE}
              color="red"
              tooltip="Comment contains restricted language"
            />
          );
          break;
        case Tag.VIDEO:
          component = (
            <TagBadge
              key={Tag.VIDEO}
              type={Tag.VIDEO}
              color="red"
              tooltip="Comment contains video"
            />
          );
          break;
        case Tag.INVALID_CHARACTERS:
        case Tag.ZALGO:
        case Tag.NON_UNICODE:
          if (foundInvalidCharTag) {
            break;
          }
          foundInvalidCharTag = true;
          component = (
            <TagBadge
              key={Tag.INVALID_CHARACTERS}
              type={Tag.INVALID_CHARACTERS}
              color="red"
              tooltip="Comment contains invalid characters"
            />
          );
          break;
        case Tag.UPPERCASE:
          component = (
            <TagBadge
              key={Tag.UPPERCASE}
              type={Tag.UPPERCASE}
              color="red"
              tooltip="Comment contains uppercase characters"
            />
          );
          break;
        case Tag.OPENWEB_INCIVILITY:
          component = (
            <TagBadge
              key={Tag.OPENWEB_INCIVILITY}
              type={Tag.OPENWEB_INCIVILITY}
              color="#0ABAB5"
              tooltip="Comment suspected as incivil"
            />
          );
          break;
        case Tag.ANIMATION:
          component = (
            <TagBadge
              key={Tag.ANIMATION}
              type={Tag.ANIMATION}
              color="red"
              tooltip="Comment contains GIF"
            />
          );
          break;
        case Tag.USER_INCIVILITY_HIGH:
          component = (
            <TagBadge
              key={Tag.USER_INCIVILITY_HIGH}
              type={Tag.USER_INCIVILITY_HIGH}
              color="red"
              text={convertCase(tag)}
              tooltip="The user history contains toxic activity in high risk"
            />
          );
          break;
        case Tag.USER_INCIVILITY_MEDIUM:
          component = (
            <TagBadge
              key={Tag.USER_INCIVILITY_MEDIUM}
              type={Tag.USER_INCIVILITY_MEDIUM}
              color="red"
              text={convertCase(tag)}
              tooltip="The user history contains toxic activity in medium risk"
            />
          );
          break;
        case Tag.EMOJIS:
          component = (
            <TagBadge
              key={Tag.EMOJIS}
              type={Tag.EMOJIS}
              color="red"
              tooltip="Comment contains emojis"
            />
          );
          break;
        case Tag.OPENWEB_OVERTURN_MODEL:
          component = (
            <TagBadge
              key={Tag.EMOJIS}
              type={Tag.EMOJIS}
              color="red"
              text={'Suspected Unsafe'}
              tooltip="Comment contains unsafe content"
            />
          );
          break;
        case Tag.FEATURED:
          component = (
            <TagBadge key={Tag.FEATURED} type={Tag.FEATURED} text={Tag.FEATURED} color="blue" />
          );
          break;
        case Tag.SUSPECTED_BOT:
          component = <TagBadge key={Tag.SUSPECTED_BOT} type={Tag.SUSPECTED_BOT} color="red" />;
          break;
        case Tag.DOXXING:
          component = (
            <TagBadge
              key={Tag.DOXXING}
              type={Tag.DOXXING}
              color="red"
              tooltip="Comment contains personal information"
            />
          );
          break;
        case Tag.SUSPENDED:
          component = (
            <TagBadge
              key={Tag.SUSPENDED}
              type={Tag.SUSPENDED}
              color="red"
              tooltip={'Suspended from conversation after 3 violations'}
            />
          );
          break;
      }

      return component;
    });
  };

  const getSelectableTags = () => {
    return selectableTags.map(tag => {
      const { label, tooltip, getAdditionalInfo } = getSelectableTagInfo(tag);
      const additionalInfo = getAdditionalInfo ? getAdditionalInfo(tagsData) : '';

      return (
        <SelectableTag
          key={tag}
          tag={tag}
          label={`${label}${additionalInfo}`}
          tooltip={tooltip}
          isSelected={selectedTag === tag}
          onClick={tag => {
            setSelectedTag(tag);
          }}
        />
      );
    });
  };

  const sortTags = (tagBadge: React.ReactElement, otherTagBadge: React.ReactElement) => {
    const {
      props: { text: tagBadgeText, type: tagBadgeType }
    } = tagBadge;
    const {
      props: { text: otherTagBadgeText, type: otherTagBadgeType }
    } = otherTagBadge;

    const firstText: string = tagBadgeText ? tagBadgeText : tagBadgeType;
    const secondText: string = otherTagBadgeText ? otherTagBadgeText : otherTagBadgeType;

    return firstText < secondText ? -1 : firstText > secondText ? 1 : 0;
  };

  const createDropdownTags = (tags: React.ReactElement[] = []) => {
    if (tags.length === 0) return null;

    return tags
      .map((tagComponent: React.ReactElement) => {
        const tagText: string =
          tagComponent.props.text || getTagText(tagComponent.props.type, tagsDataArray);
        return (
          tagText && (
            <div
              key={`more-tag-item_${tagComponent.props.type}`}
              onClick={tagComponent.props.onClick}
              className={'more-tag-item'}
            >
              <span>{tagText}</span>
              {tagComponent.props.tagData && (
                <span className={'more-tag-item-data'}>{tagComponent.props.tagData}</span>
              )}
            </div>
          )
        );
      })
      .filter((dropdownTag: React.ReactElement) => dropdownTag);
  };

  const renderTags = () => {
    const regularTags = getBaseTags()
      .concat(getPerspectiveTags())
      .concat(getOWTags())
      .filter((tagComponent: React.ReactElement) => tagComponent)
      .sort(sortTags);

    let allTags: React.ReactElement[] = [...getSelectableTags(), ...regularTags];

    const moreThanMaxTags: boolean = allTags.length > NUMBER_OF_TAGS_TO_DISPLAY;

    if (moreThanMaxTags) {
      const visibleTags = allTags.slice(0, NUMBER_OF_TAGS_TO_DISPLAY);
      const dropdownTags = createDropdownTags(allTags.slice(NUMBER_OF_TAGS_TO_DISPLAY));
      allTags = [
        ...visibleTags,
        <span key="more-tags-dropdown" className="more-tags">
          <DropdownLink
            className="tags-item"
            items={dropdownTags}
            text={`...+${dropdownTags.length} more`}
          />
        </span>
      ];
    }

    return allTags;
  };

  return (
    <>
      <div className="tags">{renderTags()}</div>
      <TagInfo tag={selectedTag} metadata={tagsData} />
    </>
  );
};

export default MessageTags;
